import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Spinner, Alert, Badge, Button as BootstrapButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faWallet,
    faArrowLeft,
    faInfoCircle,
    faCalendarAlt,
    faUser,
    faArrowUp,
    faArrowDown,
    faExchangeAlt
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useBreakpoints } from "providers/BreakpointsProvider";
import Button from "components/base/Button";
import { useConta } from "hooks/useContas";
import { contaBreadcrumbItems } from "data/conta";
import ExtratoTable from "components/tables/ExtratoTable";

// Componentes extraídos
import BotaoEditarConta from "../components/BotaoEditarConta";
import BotaoExcluirConta from "../components/BotaoExcluirConta";
import BotaoTransferir from "../components/BotaoTransferir";
import BotaoSacar from "../components/BotaoSacar";
import BotaoDepositar from "../components/BotaoDepositar";

const ContaDetalhes: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const contaId = id || "";

    // Hooks
    const contaQuery = useConta(contaId);
    const { breakpoints } = useBreakpoints();

    // Verificações responsivas
    const isMobile = breakpoints.down('md');

    // Formatação de valores
    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    // Formatação de datas
    const formatarData = (data: string) => {
        return new Date(data).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };
    
    // Loading da conta
    if (contaQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    // Erro ao carregar conta
    if (contaQuery.isError) {
        return (
            <div>
                <PageBreadcrumb items={contaBreadcrumbItems(contaId)} />
                <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                    <div className="d-flex align-items-center text-danger-emphasis">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Erro ao carregar conta. Tente novamente mais tarde.
                    </div>
                </Alert>
            </div>
        );
    }

    const conta = contaQuery.data;

    if (!conta) {
        return (
            <div>
                <PageBreadcrumb items={contaBreadcrumbItems(contaId)} />
                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle">
                    <div className="d-flex align-items-center text-warning-emphasis">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Conta não encontrada.
                    </div>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={contaBreadcrumbItems(contaId, conta.nome)} />

            {/* Header seguindo padrão exato do projeto */}
            <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                <div className="d-flex align-items-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate('/financeiro/contas')}
                        className="me-3"
                        title="Voltar para lista de contas"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>

                    <div>
                        <h2 className="mb-2">{conta.nome}</h2>
                        <h5 className="text-body-tertiary fw-semibold">
                            Gerencie movimentações e visualize o extrato desta conta
                        </h5>
                    </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                    <BotaoDepositar conta={conta} title="Depositar na conta" variant="success" showText={true} />
                    <BotaoSacar conta={conta} title="Sacar da conta" variant="warning" showText={true} />
                    <BotaoTransferir conta={conta} title="Transferir para outra conta" variant="info" showText={true} />

                    <div className="vr mx-2"></div>

                    <BotaoEditarConta conta={conta} />
                    <BotaoExcluirConta conta={conta} />
                </div>
            </div>

            {/* Informações da conta */}
            <Card className="mb-4 border border-opacity-25">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex justify-content-center align-items-center me-3"
                                    style={{
                                        width: "60px",
                                        height: "60px",
                                        backgroundColor: conta.saldo >= 0 ? "var(--bs-success-bg-subtle)" : "var(--bs-danger-bg-subtle)",
                                        borderRadius: "12px",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faWallet}
                                        size="lg"
                                        className={conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}
                                    />
                                </div>
                                <div>
                                    <h5 className="mb-1">{conta.nome}</h5>
                                    <p className="text-body-secondary mb-0">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                        Criada em {formatarData(conta.createdAt)}
                                    </p>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end">
                            <div className="small text-body-secondary mb-1">Saldo Atual</div>
                            <div className={`h3 mb-2 ${conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                                {formatarMoeda(conta.saldo)}
                            </div>
                            <Badge
                                bg={conta.saldo >= 0 ? 'success' : 'danger'}
                                className="px-3 py-2"
                            >
                                {conta.saldo >= 0 ? 'Saldo Positivo' : 'Saldo Negativo'}
                            </Badge>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>



            {/* Extrato */}
            <Card className="border border-opacity-25">
                <Card.Header>
                    <h5 className="mb-0 text-body-emphasis">
                        <FontAwesomeIcon icon={faWallet} className="me-2" />
                        Extrato da Conta
                    </h5>
                </Card.Header>

                <Card.Body className={isMobile ? 'p-0' : ''}>
                    <ExtratoTable />
                </Card.Body>
            </Card>

            {/* Footer com botões seguindo padrão do projeto */}
            <div className="d-flex flex-wrap gap-3 flex-between-end mb-5 mt-4">
                <Button
                    variant="secondary"
                    type="button"
                    onClick={() => navigate('/financeiro/contas')}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Voltar para Contas
                </Button>
                <div className="d-flex flex-wrap gap-2">
                    <BotaoDepositar conta={conta} title="Depositar na conta" variant="success" showText={true} />
                    <BotaoSacar conta={conta} title="Sacar da conta" variant="warning" showText={true} />
                    <BotaoTransferir conta={conta} title="Transferir para outra conta" variant="info" showText={true} />

                    <div className="vr mx-2"></div>

                    <BotaoEditarConta conta={conta} />
                    <BotaoExcluirConta conta={conta} />
                </div>
            </div>
        </div>
    );
};

export default ContaDetalhes; 